import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  HStack,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Divider,
  Icon,
  useColorModeValue,
  Flex,
  Select,
  Tooltip
} from '@chakra-ui/react';
import { ThemeTemplate, ColorModeType, BackgroundType, TransitionType, useLyricsTheme } from '../context/LyricsThemeContext';
import { FontSizeIcon, LineSpacingIcon, GradientIcon, SolidColorIcon, CheckIcon } from './IconComponents';

interface TemplateEditorProps {
  template?: ThemeTemplate;
  onSave: (template: Omit<ThemeTemplate, 'id' | 'isDefault'>) => void;
  onCancel: () => void;
}

// Define a transition icon
const TransitionIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 8l-4 4h3c0 3.31-2.69 6-6 6-1.01 0-1.97-.25-2.8-.7l-1.46 1.46C8.97 19.54 10.43 20 12 20c4.42 0 8-3.58 8-8h3l-4-4zM6 12c0-3.31 2.69-6 6-6 1.01 0 1.97.25 2.8.7l1.46-1.46C15.03 4.46 13.57 4 12 4c-4.42 0-8 3.58-8 8H1l4 4 4-4H6z" />
  </svg>
);

// Create a simple ScaleIcon component since it seems missing from the imports
const ScaleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M4 16h16V6H4v10zm2-8h12v6H6V8z"/>
  </svg>
);

// Define a frosted effect icon
const FrostedEffectIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
    <rect x="6" y="6" width="12" height="12" rx="1" fill="currentColor" fillOpacity="0.2" />
  </svg>
);

const TemplateEditor: React.FC<TemplateEditorProps> = ({
  template,
  onSave,
  onCancel
}) => {
  // Local state for template properties
  const [name, setName] = useState(template?.name || 'New Template');
  const [colorMode, setColorMode] = useState<ColorModeType>(template?.colorMode || 'dark');
  const [fontSize, setFontSize] = useState(template?.fontSize || 1);
  const [lineSpacing, setLineSpacing] = useState(template?.lineSpacing || 1);
  const [backgroundType, setBackgroundType] = useState<BackgroundType>(template?.backgroundType || 'gradient');
  const [backgroundColor, setBackgroundColor] = useState(template?.backgroundColor || '#121212');
  const [activeTextColor, setActiveTextColor] = useState(template?.activeTextColor || '#ffffff');
  const [inactiveTextColor, setInactiveTextColor] = useState(template?.inactiveTextColor || 'rgba(255, 255, 255, 0.6)');
  const [currentLineScale, setCurrentLineScale] = useState(template?.currentLineScale || 1.1);
  const [currentLineFontFactor, setCurrentLineFontFactor] = useState(template?.currentLineFontFactor || 0.7);
  const [currentLineFontWeight, setCurrentLineFontWeight] = useState(template?.currentLineFontWeight || 0.6);
  const [letterSpacing, setLetterSpacing] = useState(template?.letterSpacing || '-0.04em');
  const [transitionType, setTransitionType] = useState<TransitionType>(template?.transitionType || 'bounce');
  const [frostedEffect, setFrostedEffect] = useState(template?.frostedEffect !== undefined ? template?.frostedEffect : false);
  const [frostedOpacity, setFrostedOpacity] = useState(template?.frostedOpacity !== undefined ? template?.frostedOpacity : 0.3);
  const [frostedColorDark, setFrostedColorDark] = useState(template?.frostedColorDark || 'rgba(25, 25, 35, 0.3)');
  const [frostedColorLight, setFrostedColorLight] = useState(template?.frostedColorLight || 'rgba(250, 250, 255, 0.3)');

  // UI color values
  const textColor = useColorModeValue('gray.700', 'white');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const iconColor = useColorModeValue('gray.600', 'gray.300');

  // Update state when template changes
  useEffect(() => {
    if (template) {
      setName(template.name);
      setColorMode(template.colorMode);
      setFontSize(template.fontSize);
      setLineSpacing(template.lineSpacing);
      setBackgroundType(template.backgroundType);
      setBackgroundColor(template.backgroundColor);
      
      // Add fallbacks for possible missing color properties in older templates
      if (template.activeTextColor) {
        setActiveTextColor(template.activeTextColor);
      } else {
        // If missing, set based on color mode
        setActiveTextColor(template.colorMode === 'dark' ? '#ffffff' : '#000000');
      }
      
      if (template.inactiveTextColor) {
        setInactiveTextColor(template.inactiveTextColor);
      } else {
        // If missing, set based on color mode
        setInactiveTextColor(template.colorMode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)');
      }
      
      // Set new properties with defaults
      setCurrentLineScale(template.currentLineScale || 1.1);
      setCurrentLineFontFactor(template.currentLineFontFactor || 0.7);
      setCurrentLineFontWeight(template.currentLineFontWeight || 0.6);
      setLetterSpacing(template.letterSpacing || '-0.04em');
      setTransitionType(template.transitionType || 'bounce');
      setFrostedEffect(template.frostedEffect !== undefined ? template.frostedEffect : false);
      setFrostedOpacity(template.frostedOpacity !== undefined ? template.frostedOpacity : 0.3);
      setFrostedColorDark(template.frostedColorDark || 'rgba(25, 25, 35, 0.3)');
      setFrostedColorLight(template.frostedColorLight || 'rgba(250, 250, 255, 0.3)');
    }
  }, [template]);

  // Handle save
  const handleSave = () => {
    // Log the font weight value being saved
    console.log('Saving template with font weight:', currentLineFontWeight);
    
    // Create the template data
    const templateData = {
      name,
      colorMode,
      fontSize,
      lineSpacing,
      multiplier: template?.multiplier ?? 0.3, // Keep existing multiplier or use default
      backgroundType,
      backgroundColor,
      activeTextColor,
      inactiveTextColor,
      currentLineScale,
      currentLineFontFactor,
      currentLineFontWeight,
      transitionType,
      frostedEffect,
      frostedOpacity,
      frostedColorDark,
      frostedColorLight,
      letterSpacing
    };
    
    // Log the complete template data
    console.log('Template data to save:', templateData);
    
    // Save the template
    onSave(templateData);
  };

  // Sample lyrics text
  const sampleLyrics = [
    "This is a past lyric line its really long",
    "This is the current lyric line, this is a long lyric line",
    "This is a future lyric line and it is a long lyric line"
  ];

  return (
    <Box p={4} color={textColor}>
      <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
        {/* Left panel - Settings - Now scrollable */}
        <Box 
          flex={{ base: '1', md: '3' }}
          maxHeight={{ md: '70vh' }}
          overflowY={{ md: 'auto' }}
          pr={{ md: 4 }}
          className="template-editor-scrollable"
        >
          <VStack spacing={6} align="stretch">
            {/* Template name */}
            <FormControl id="templateName">
              <FormLabel fontWeight="bold">Template Name</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter a name for this template"
              />
            </FormControl>

            <Divider />

            {/* Text Colors - Now in columns instead of rows */}
            <Flex gap={5} direction={{ base: 'column', sm: 'row' }}>
              {/* Active Lyric Color */}
              <FormControl>
                <FormLabel fontWeight="bold">Active Lyric Color</FormLabel>
                <VStack align="stretch" spacing={2}>
                  <Flex align="center">
                    <Input
                      type="color"
                      value={activeTextColor}
                      onChange={(e) => setActiveTextColor(e.target.value)}
                      width="80px"
                      mr={2}
                    />
                    <Box 
                      width="24px" 
                      height="24px" 
                      borderRadius="md" 
                      bg={activeTextColor}
                      borderWidth="1px"
                      borderColor="gray.300"
                    />
                  </Flex>
                  <Input
                    value={activeTextColor}
                    onChange={(e) => setActiveTextColor(e.target.value)}
                    placeholder="Active lyric color"
                  />
                </VStack>
              </FormControl>
              
              {/* Inactive Lyric Color */}
              <FormControl>
                <FormLabel fontWeight="bold">Inactive Lyric Color</FormLabel>
                <VStack align="stretch" spacing={2}>
                  <Flex align="center">
                    <Input
                      type="color"
                      value={inactiveTextColor}
                      onChange={(e) => setInactiveTextColor(e.target.value)}
                      width="80px"
                      mr={2}
                    />
                    <Box 
                      width="24px" 
                      height="24px" 
                      borderRadius="md" 
                      bg={inactiveTextColor}
                      borderWidth="1px"
                      borderColor="gray.300"
                    />
                  </Flex>
                  <Input
                    value={inactiveTextColor}
                    onChange={(e) => setInactiveTextColor(e.target.value)}
                    placeholder="Inactive lyric color"
                  />
                </VStack>
                <Text fontSize="xs" mt={1} color={mutedTextColor}>
                  Use the color picker to select a color
                </Text>
              </FormControl>
            </Flex>

            <Divider />

            {/* Font Size - using radio buttons instead of dropdown */}
            <FormControl>
              <FormLabel fontWeight="bold">Font Size</FormLabel>
              <HStack align="center">
                <Icon as={FontSizeIcon} color={iconColor} mr={2} />
                <RadioGroup
                  value={fontSize.toString()}
                  onChange={(value) => setFontSize(parseFloat(value))}
                  colorScheme="blue"
                >
                  <Stack direction={["column", "row"]} spacing={2} wrap="wrap">
                    <Radio value="0.7">Small</Radio>
                    <Radio value="1">Medium (Default)</Radio>
                    <Radio value="1.2">Large</Radio>
                    <Radio value="1.3">Extra Large</Radio>

                  </Stack>
                </RadioGroup>
              </HStack>
            </FormControl>

            {/* Line Spacing - using radio buttons instead of dropdown */}
            <FormControl>
              <FormLabel fontWeight="bold">Line Spacing</FormLabel>
              <HStack align="center">
                <Icon as={LineSpacingIcon} color={iconColor} mr={2} />
                <RadioGroup
                  value={lineSpacing.toString()}
                  onChange={(value) => setLineSpacing(parseFloat(value))}
                  colorScheme="blue"
                >
                  <Stack direction={["column", "row"]} spacing={2} wrap="wrap">
                    <Radio value="0.7">Tight</Radio>
                    <Radio value="1">Normal (Default)</Radio>
                    <Radio value="1.3">Relaxed</Radio>
                  </Stack>
                </RadioGroup>
              </HStack>
            </FormControl>

            <Divider />
            
            {/* Current Line Scale - using slider */}
            <FormControl>
              <FormLabel fontWeight="bold">Current Line Scale</FormLabel>
              <HStack align="center">
                <Icon as={ScaleIcon} color={iconColor} mr={2} />
                <Box width="100%">
                  <Text fontSize="sm" mb={1}>Scale: {currentLineScale.toFixed(2)}</Text>
                  <Slider
                    aria-label="current-line-scale-slider"
                    defaultValue={currentLineScale}
                    min={1.0}
                    max={1.5}
                    step={0.05}
                    colorScheme="blue"
                    onChange={(v) => setCurrentLineScale(v)}
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </Box>
              </HStack>
              <Text fontSize="xs" mt={1} color={mutedTextColor}>
                How much the current lyric line is scaled up
              </Text>
            </FormControl>
            
            {/* Current Line Font Size Factor - using slider */}
            <FormControl mt={4}>
              <FormLabel fontWeight="bold">Current Line Font Size</FormLabel>
              <HStack align="center">
                <Icon as={FontSizeIcon} color={iconColor} mr={2} />
                <Box width="100%">
                  <Text fontSize="sm" mb={1}>
                    Font Size Factor: {currentLineFontFactor === 0 ? "Same as other lines" : currentLineFontFactor.toFixed(2)}
                  </Text>
                  <Slider
                    aria-label="current-line-font-factor-slider"
                    defaultValue={currentLineFontFactor}
                    min={0}
                    max={1.0}
                    step={0.05}
                    colorScheme="blue"
                    onChange={(v) => setCurrentLineFontFactor(v)}
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </Box>
              </HStack>
              <Text fontSize="xs" mt={1} color={mutedTextColor}>
                At minimum, current line has same font size as other lines. At maximum, it's significantly larger.
              </Text>
            </FormControl>
            
            {/* Current Line Font Weight - using slider */}
            <FormControl mt={4}>
              <FormLabel fontWeight="bold">Current Line Font Weight</FormLabel>
              <HStack align="center">
                <Icon as={FontSizeIcon} color={iconColor} mr={2} />
                <Box width="100%">
                  <Text fontSize="sm" mb={1}>
                    Font Weight: {currentLineFontWeight === 0 ? "Normal" : currentLineFontWeight.toFixed(2)}
                  </Text>
                  <Slider
                    aria-label="current-line-font-weight-slider"
                    defaultValue={currentLineFontWeight}
                    min={0}
                    max={1.0}
                    step={0.05}
                    colorScheme="blue"
                    onChange={(v) => {
                      console.log('Font weight changed to:', v);
                      setCurrentLineFontWeight(v);
                    }}
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </Box>
              </HStack>
              <Text fontSize="xs" mt={1} color={mutedTextColor}>
                Controls the font weight of the current line
              </Text>
            </FormControl>
            
            {/* Transition Type - using radio buttons */}
            <FormControl mt={4}>
              <FormLabel fontWeight="bold">Transition Effect</FormLabel>
              <HStack align="center">
                <Icon as={TransitionIcon} color={iconColor} mr={2} />
                <RadioGroup
                  value={transitionType}
                  onChange={(value: TransitionType) => setTransitionType(value)}
                  colorScheme="blue"
                >
                  <Stack direction={["column", "row"]} spacing={2} wrap="wrap">
                    <Radio value="bounce">Bounce</Radio>
                    <Radio value="smooth">Smooth</Radio>
                    <Radio value="sharp">Sharp</Radio>
                    <Radio value="none">None</Radio>
                  </Stack>
                </RadioGroup>
              </HStack>
              <Text fontSize="xs" mt={1} color={mutedTextColor}>
                Animation style when transitioning between lyrics
              </Text>
            </FormControl>

            <Divider />
            
            {/* Frosted Effect Controls */}
            <FormControl mt={4}>
              <FormLabel fontWeight="bold">Frosted Effect</FormLabel>
              <HStack align="center" mb={2}>
                <Icon as={FrostedEffectIcon} color={iconColor} mr={2} />
                <Switch 
                  isChecked={frostedEffect}
                  onChange={(e) => setFrostedEffect(e.target.checked)}
                  colorScheme="blue"
                  size="md"
                />
                <Text ml={2} fontSize="sm">{frostedEffect ? "Enabled" : "Disabled"}</Text>
              </HStack>
              
              {frostedEffect && (
                <Box mt={3}>
                  <Text fontSize="sm" mb={1}>Frosted Opacity: {Math.round(frostedOpacity * 100)}%</Text>
                  <Slider
                    aria-label="frosted-opacity-slider"
                    value={frostedOpacity}
                    min={0.05}
                    max={0.8}
                    step={0.05}
                    colorScheme="blue"
                    onChange={(v) => setFrostedOpacity(v)}
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                  <Text fontSize="xs" mt={1} mb={3} color={mutedTextColor}>
                    Controls how transparent the frosted effect appears
                  </Text>
                  
                  <Text fontSize="sm" mb={1}>Dark Mode Color</Text>
                  <HStack>
                    <Input
                      type="color"
                      value={frostedColorDark.startsWith('rgba') 
                        ? activeTextColor
                        : frostedColorDark}
                      onChange={(e) => {
                        // Convert hex to rgba with current opacity
                        const rgba = activeTextColor.startsWith('rgba') ? activeTextColor : `rgba(${parseInt(e.target.value.slice(1, 3), 16)}, ${parseInt(e.target.value.slice(3, 5), 16)}, ${parseInt(e.target.value.slice(5, 7), 16)}, ${frostedOpacity})`;
                        setFrostedColorDark(rgba);
                      }}
                      width="80px"
                    />
                    <Input
                      value={frostedColorDark}
                      onChange={(e) => setFrostedColorDark(e.target.value)}
                      placeholder="rgba(25, 25, 35, 0.3)"
                    />
                  </HStack>
                  <Text fontSize="xs" mt={1} mb={3} color={mutedTextColor}>
                    Color for frosted effect in dark mode
                  </Text>
                  
                  <Text fontSize="sm" mb={1}>Light Mode Color</Text>
                  <HStack>
                    <Input
                      type="color"
                      value={frostedColorLight.startsWith('rgba') 
                        ? activeTextColor
                        : frostedColorLight}
                      onChange={(e) => {
                        // Convert hex to rgba with current opacity
                        const rgba = activeTextColor.startsWith('rgba') ? activeTextColor : `rgba(${parseInt(e.target.value.slice(1, 3), 16)}, ${parseInt(e.target.value.slice(3, 5), 16)}, ${parseInt(e.target.value.slice(5, 7), 16)}, ${frostedOpacity})`;
                        setFrostedColorLight(rgba);
                      }}
                      width="80px"
                    />
                    <Input
                      value={frostedColorLight}
                      onChange={(e) => setFrostedColorLight(e.target.value)}
                      placeholder="rgba(250, 250, 255, 0.3)"
                    />
                  </HStack>
                  <Text fontSize="xs" mt={1} color={mutedTextColor}>
                    Color for frosted effect in light mode
                  </Text>
                </Box>
              )}
              
              <Text fontSize="xs" mt={1} color={mutedTextColor}>
                {frostedEffect 
                  ? "Apple Music style frosted glass background effect" 
                  : "Enable to add an Apple Music style frosted glass effect"}
              </Text>
            </FormControl>

            <Divider />

            {/* Background Type */}
            <FormControl as="fieldset">
              <FormLabel as="legend" fontWeight="bold">Background Type</FormLabel>
              <RadioGroup value={backgroundType} onChange={(value: BackgroundType) => setBackgroundType(value)}>
                <Stack direction="row" spacing={5}>
                  <Radio value="gradient">
                    <HStack spacing={2}>
                      <Icon as={GradientIcon} color={iconColor} />
                      <Text>Album Gradient</Text>
                    </HStack>
                  </Radio>
                  <Radio value="solid">
                    <HStack spacing={2}>
                      <Icon as={SolidColorIcon} color={iconColor} />
                      <Text>Solid Color</Text>
                    </HStack>
                  </Radio>
                </Stack>
              </RadioGroup>
            </FormControl>

            {/* Background Color (only for solid) */}
            {backgroundType === 'solid' && (
              <FormControl>
                <FormLabel fontWeight="bold">Background Color</FormLabel>
                <HStack>
                  <Input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    width="80px"
                  />
                  <Input
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    placeholder="#000000"
                  />
                </HStack>
              </FormControl>
            )}

            {/* Letter Spacing control */}
            <FormControl mt={4}>
              <FormLabel fontWeight="bold">Letter Spacing</FormLabel>
              <HStack align="center">
                <Icon as={FontSizeIcon} color={iconColor} mr={2} />
                <Input
                  value={letterSpacing}
                  onChange={(e) => setLetterSpacing(e.target.value)}
                  placeholder="-0.04em"
                  width="150px"
                />
              </HStack>
              <Text fontSize="xs" mt={1} color={mutedTextColor}>
                Controls the spacing between letters (e.g. -0.04em, normal, 0.1em)
              </Text>
            </FormControl>
          </VStack>
        </Box>
        
        {/* Right panel - Preview */}
        <Box 
          flex={{ base: '1', md: '2' }} 
          position={{ md: 'sticky' }} 
          top={{ md: '0' }}
          alignSelf="flex-start"
        >
          <Text fontWeight="bold" mb={2}>Preview</Text>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={4}
            height="350px"
            position="relative"
            overflow="hidden"
            bg={colorMode === 'dark' ? 'gray.800' : 'white'}
          >
            {/* Background preview */}
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              opacity={0.2}
              bg={backgroundType === 'gradient' 
                ? `linear-gradient(135deg, ${backgroundColor}, rgba(0,0,0,0))` 
                : backgroundColor}
              zIndex={0}
            />
            
            {/* Sample lyrics */}
            <VStack
              spacing={lineSpacing * 8}
              align="flex-start"
              mt={4}
              zIndex={2}
              position="relative"
            >
              {sampleLyrics.map((line, index) => (
                <Text
                  key={index}
                  fontSize={index === 1 
                    ? `${16 * fontSize * (1 + currentLineFontFactor)}px` 
                    : `${16 * fontSize}px`}
                  opacity={index === 0 ? 0.5 : index === 1 ? 1 : 0.7}
                  fontWeight={index === 1 ? 'bold' : 'normal'}
                  color={index === 1 
                    ? activeTextColor
                    : inactiveTextColor}
                  transform={index === 1 ? 'scale(1.05)' : 'none'}
                  letterSpacing={letterSpacing}
                  transition="color 0.3s ease-in-out"
                  position="relative"
                  sx={index === 1 && frostedEffect ? {
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '-12px',
                      left: '-20px',
                      right: '-20px',
                      bottom: '-12px',
                      borderRadius: 'md',
                      bg: colorMode === 'dark'
                        ? frostedColorDark
                        : frostedColorLight,
                      backdropFilter: 'blur(15px)',
                      boxShadow: colorMode === 'dark'
                        ? 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                        : 'inset 0 0 0 1px rgba(0, 0, 0, 0.05)',
                      zIndex: -1,
                      transition: 'all 0.5s cubic-bezier(0.23, 1, 0.32, 1)'
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: '-12px',
                      left: '-20px',
                      right: '-20px',
                      height: '50%',
                      bg: 'rgba(255, 255, 255, 0.05)',
                      borderTopLeftRadius: 'md',
                      borderTopRightRadius: 'md',
                      zIndex: -1
                    }
                  } : undefined}
                >
                  {line}
                </Text>
              ))}
            </VStack>
          </Box>
          <Text fontSize="xs" mt={1} color={mutedTextColor} textAlign="center">
            Preview updates in real-time as you adjust settings
          </Text>
          <Divider p={2} />
          {/* Actions */}
          <HStack spacing={4} mt={4} justify="right" align="bottom">
            <Button variant="outline" onClick={onCancel} mr={2}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSave} leftIcon={<CheckIcon />}>
              {template ? "Update Template" : "Save Template"}
            </Button>
          </HStack>
        </Box>
      </Flex>
    </Box>
  );
};

export default TemplateEditor; 